// ERRORS-1 Part B: React Query hooks for `/v1/support/bug-reports`. Kept out of queries.ts
// (already large). Only mounted/enabled while the `bug_reports` feature is on
// (`useFeatureEnabled`, FEATURES-1).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BugReport,
  BugReportPreview,
  CreateBugReportRequest,
} from '@scribe/core/types';
import { useApi } from './ApiContext';
import { useFeatureEnabled } from './features';
import { isMock } from './mock';

// Mock mode has no backend: fabricate plausible responses so the dialog is demoable.
function mockReport(body: CreateBugReportRequest): BugReport {
  return {
    id: `bug-mock-${Date.now()}`,
    status: 'pending',
    recording_id: body.recording_id ?? null,
    failure_code: body.failure_code ?? null,
    area: body.area ?? 'web',
    created_at: new Date().toISOString(),
    delivered_at: null,
    github_issue_number: null,
    github_issue_url: null,
  };
}

function mockPreview(body: CreateBugReportRequest): BugReportPreview {
  return {
    title: `[app] ${body.area ?? 'web'}: ${body.failure_code ?? 'bug report'} — ${body.what_happened.slice(0, 70)}`,
    body: `### What happened\n> ${body.what_happened}\n\n(mock preview — no server scrubbing in mock mode)`,
    labels: ['bug', 'from-app', `area:${body.area ?? 'web'}`],
  };
}

export function useBugReportsForRecording(recordingId: string | undefined) {
  const { client } = useApi();
  const bugReportsOn = useFeatureEnabled('bug_reports');
  return useQuery({
    queryKey: ['bug-reports', recordingId],
    enabled: bugReportsOn && !!recordingId && !isMock(),
    queryFn: () => client.listBugReports({ recording_id: recordingId! }),
    // A pending report turns into "Reported as #123" once the worker delivers it.
    refetchInterval: (query) =>
      query.state.data?.items.some((r) => r.status === 'pending' || r.status === 'delivering')
        ? 30_000
        : false,
  });
}

export function usePreviewBugReport() {
  const { client } = useApi();
  return useMutation({
    mutationFn: (body: CreateBugReportRequest) =>
      isMock() ? Promise.resolve(mockPreview(body)) : client.previewBugReport(body),
  });
}

export function useCreateBugReport() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBugReportRequest) =>
      isMock() ? Promise.resolve(mockReport(body)) : client.createBugReport(body),
    onSuccess: (report) => {
      qc.invalidateQueries({ queryKey: ['bug-reports', report.recording_id ?? undefined] });
    },
  });
}
